/**
 * Dynamic Price Button
 * 
 * Displays calculated price (variant price × quantity) in Add to Cart button
 * Format: "Add to cart · £27.50"
 * 
 * Features:
 * - Updates on variant/quantity changes
 * - Optional quantity reset on variant change (customizer setting)
 * - Handles DOM replacement during product:rerender
 * - Preserves sold out/unavailable/pre-order states
 */
(() => {
  const SELECTORS = {
    mainButton: '#AddToCart',
    stickyButton: '#StickyAddToCart',
    productForm: 'form[is="product-form"]',
    quantityInput: '[data-block-type="quantity-selector"] .quantity-selector__input',
    variantScript: 'variant-picker script[data-variant]',
    buyButtonsBlock: '[data-block-type="buy-buttons"]'
  };

  const STATE = {
    currentVariant: null,
    currentQuantity: 1,
    enabled: false,
    baseButtonText: ''
  };

  // Price formatting using theme's formatMoney function
  function formatPrice(priceInCents) {
    try {
      if (typeof formatMoney === 'function') {
        return formatMoney(priceInCents, window.themeVariables?.settings?.moneyFormat);
      }
    } catch (e) {}
    
    // Fallback: Replace {{amount}} placeholder in money format with actual number
    try {
      const moneyFormat = window.themeVariables?.settings?.moneyFormat;
      if (moneyFormat) {
        const formattedNumber = (priceInCents / 100).toFixed(2);
        return moneyFormat.replace(/\{\{\s*amount\s*\}\}/gi, formattedNumber);
      }
    } catch (e) {}
    
    // Last resort: Return number only
    return (priceInCents / 100).toFixed(2);
  }

  function getVariantData() {
    const variantScript = document.querySelector(SELECTORS.variantScript);
    if (!variantScript) return null;
    try {
      return JSON.parse(variantScript.textContent);
    } catch (e) {
      return null;
    }
  }

  function getCurrentQuantity() {
    const quantityInput = document.querySelector(SELECTORS.quantityInput);
    return quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;
  }

  function calculateTotalPrice() {
    if (!STATE.currentVariant || !STATE.currentVariant.available) return null;
    return STATE.currentVariant.price * STATE.currentQuantity;
  }

  // Check if button is in "add to cart" state (not sold out/unavailable/pre-order)
  function isAddToCartState(button) {
    const content = (button.getAttribute('data-button-content') || button.textContent || '').toLowerCase();
    return content && 
      !content.includes('sold out') && 
      !content.includes('unavailable') && 
      !content.includes('pre-order');
  }

  function getButtonText() {
    if (!STATE.enabled) return STATE.baseButtonText;
    const total = calculateTotalPrice();
    if (total === null) return STATE.baseButtonText;
    return `${STATE.baseButtonText} · ${formatPrice(total)}`;
  }

  function captureBaseButtonText() {
    const main = document.querySelector(SELECTORS.mainButton);
    
    // Try to get text from button first
    if (main) {
      const buttonText = main.getAttribute('data-button-content') || main.textContent.trim();
      if (buttonText) return buttonText;
    }
    
    // Fallback to theme's translation string
    try {
      const translatedText = window.themeVariables?.strings?.productFormAddToCart;
      if (translatedText) return translatedText;
    } catch (e) {}
    
    // Last resort fallback (should rarely happen)
    return 'Add to cart';
  }

  function updateButtons() {
    if (!STATE.enabled) return;
    
    const buttons = [
      document.querySelector(SELECTORS.mainButton),
      document.querySelector(SELECTORS.stickyButton)
    ].filter(Boolean);

    if (buttons.length === 0) return;

    const text = getButtonText();
    buttons.forEach((btn) => {
      if (!isAddToCartState(btn)) return;
      btn.textContent = text;
      btn.setAttribute('data-button-content', text);
    });
  }

  function onVariantChanged(event) {
    if (event.detail?.variant) {
      STATE.currentVariant = event.detail.variant;
    }

    const buyButtons = document.querySelector(SELECTORS.buyButtonsBlock);
    const shouldResetQuantity = buyButtons?.getAttribute('data-reset-quantity') === 'true';

    // Delay to allow product:rerender to complete DOM replacement
    setTimeout(() => {
      // Reset quantity if setting enabled (improves price comparison UX)
      if (shouldResetQuantity) {
        const quantityInput = document.querySelector(SELECTORS.quantityInput);
        if (quantityInput) {
          const currentValue = parseInt(quantityInput.value, 10) || 1;
          if (currentValue !== 1) {
            quantityInput.value = '1';
            quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      STATE.currentQuantity = getCurrentQuantity();
      
      // Re-capture base text in case buttons were replaced during rerender
      const newBaseText = captureBaseButtonText();
      if (newBaseText && !newBaseText.includes(' · ')) {
        STATE.baseButtonText = newBaseText.trim();
      }

      updateButtons();
    }, 100);
  }

  function onProductRerender() {
    // Delay to allow DOM replacement to complete
    setTimeout(() => {
      const domVariant = getVariantData();
      if (domVariant && domVariant.id) {
        STATE.currentVariant = domVariant;
      } else if (!STATE.currentVariant) {
        return;
      }

      STATE.currentQuantity = getCurrentQuantity();

      // Extract base text from potentially price-suffixed button text
      const newBaseText = captureBaseButtonText();
      if (newBaseText) {
        const priceSuffixMatch = newBaseText.match(/^(.+?)\s·\s[£$€]/);
        if (priceSuffixMatch && priceSuffixMatch[1]) {
          STATE.baseButtonText = priceSuffixMatch[1].trim();
        } else if (!newBaseText.includes(' · ') && newBaseText.trim() !== STATE.baseButtonText) {
          STATE.baseButtonText = newBaseText.trim();
        }
      }

      updateButtons();
    }, 50);
  }

  function onQuantityChange(event) {
    const q = parseInt(event.target.value, 10) || 1;
    if (q === STATE.currentQuantity) return;
    STATE.currentQuantity = q;
    updateButtons();
  }

  function init() {
    const form = document.querySelector(SELECTORS.productForm);
    const buyButtons = document.querySelector(SELECTORS.buyButtonsBlock);
    if (!form || !buyButtons) return;

    // Check if feature is enabled via customizer setting
    STATE.enabled = buyButtons.getAttribute('data-show-button-price') === 'true';
    if (!STATE.enabled) return;

    STATE.baseButtonText = captureBaseButtonText();
    STATE.currentVariant = getVariantData();
    STATE.currentQuantity = getCurrentQuantity();

    if (STATE.currentVariant) updateButtons();

    // Document-level listeners survive DOM replacement
    document.addEventListener('variant:changed', onVariantChanged);
    document.addEventListener('product:rerender', onProductRerender);

    // Event delegation for quantity changes (survives input replacement)
    document.addEventListener('input', (event) => {
      if (event.target.matches(SELECTORS.quantityInput)) {
        onQuantityChange(event);
      }
    });

    document.addEventListener('change', (event) => {
      if (event.target.matches(SELECTORS.quantityInput)) {
        onQuantityChange(event);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
